export type SvgBuildOptions = {
  iconMarkup: string;
  iconViewBox: string;
  iconSize: number;
  totalSize: number;
  padding: number;
  color: string;
  strokeWidth: number;
  strokeLinecap: string;
  strokeLinejoin: string;
  backgroundMode: "transparent" | "solid";
  backgroundColor: string;
  rotation?: number;
};

const parseViewBox = (viewBox: string): { width: number; height: number } => {
  const parts = viewBox.split(" ").map((value) => Number(value));
  if (parts.length !== 4 || parts.some((value) => Number.isNaN(value))) {
    return { width: 24, height: 24 };
  }
  return { width: parts[2], height: parts[3] };
};

export const buildSvgMarkup = ({
  iconMarkup,
  iconViewBox,
  iconSize,
  totalSize,
  padding,
  color,
  strokeWidth,
  strokeLinecap,
  strokeLinejoin,
  backgroundMode,
  backgroundColor,
  rotation = 0,
}: SvgBuildOptions) => {
  const { width: viewBoxWidth, height: viewBoxHeight } = parseViewBox(iconViewBox || "0 0 24 24");
  const safeIconSize = Math.max(1, iconSize);
  const scale = safeIconSize / viewBoxWidth;
  const hasBackground = backgroundMode === "solid";
  const backgroundRect = hasBackground
    ? `<rect width="100%" height="100%" fill="${backgroundColor}" />`
    : "";

  // Rotate around the center of the original icon viewBox
  const iconCenterX = viewBoxWidth / 2;
  const iconCenterY = viewBoxHeight / 2;
  const rotateTransform = rotation !== 0 
    ? `<g transform="rotate(${rotation} ${iconCenterX} ${iconCenterY})">${iconMarkup}</g>` 
    : iconMarkup;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${totalSize}" height="${totalSize}" viewBox="0 0 ${totalSize} ${totalSize}" fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="${strokeLinecap}" stroke-linejoin="${strokeLinejoin}">` +
    backgroundRect +
    `<g transform="translate(${padding} ${padding}) scale(${scale})">${rotateTransform}</g>` +
    "</svg>"
  );
};

export const downloadFile = (filename: string, dataUrl: string) => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const svgToPngDataUrl = async (svgMarkup: string, size: number) => {
  const svgBlob = new Blob([svgMarkup], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (event) => reject(event);
      img.src = url;
    });

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas context unavailable");
    }

    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(image, 0, 0, size, size);

    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
};
